import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ChefHat,
  Music,
  Zap,
  Egg,
  ArrowUpRight,
} from 'lucide-react';

const easeLuxe = [0.16, 1, 0.3, 1];

const SERVICES = [
  {
    icon: ChefHat,
    title: 'Traiteur & restauration',
    description: 'Reseau de chefs locaux selectionnes pour vos receptions, brunchs et diners de prestige.',
    link: '/traiteurs-partenaires',
  },
  {
    icon: Music,
    title: 'Animation & musique',
    description: 'DJ et animateurs partenaires pour vos mariages, anniversaires et soirees dansantes dans la salle des fetes.',
    link: '/dj-partenaires',
  },
];

export default function Services() {
  const navigate = useNavigate();

  return (
    <>
      {/* ─── Services & Experiences grid ───────────────── */}
      <section
        id="services"
        data-testid="services-section"
        className="py-28 md:py-40 bg-[#F9F8F6]"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.5 }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
            className="mb-20 md:mb-28 max-w-3xl"
          >
            <motion.span
              variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeLuxe } } }}
              className="overline"
            >
              Nos partenaires
            </motion.span>
            <motion.h2
              variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeLuxe } } }}
              data-testid="services-title"
              className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light text-[#1A1C19] mt-5 tracking-[-0.025em] leading-[0.95]"
            >
              Services
              <span className="italic text-[#5A6153]"> &amp; experiences</span>
            </motion.h2>
            <motion.p
              variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeLuxe } } }}
              className="mt-8 text-[#5A6153] max-w-xl text-base md:text-lg leading-relaxed font-light"
            >
              Pour rendre votre sejour et vos evenements memorables, nous travaillons avec un reseau
              de partenaires triés sur le volet &mdash; traiteurs et DJ selectionnes par nos soins.
            </motion.p>
          </motion.div>

          {/* Partner cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7 max-w-4xl">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.article
                  key={s.title}
                  data-testid={`service-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, ease: easeLuxe, delay: i * 0.05 }}
                  className="group relative bg-white rounded-[1.25rem] md:rounded-[1.5rem] p-8 md:p-10 border border-black/[0.04] hover:border-[#CBA77A]/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_48px_-16px_rgba(26,28,25,0.12)]"
                >
                  {/* Icon */}
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-[#1A1C19]/[0.03] flex items-center justify-center mb-8 group-hover:bg-[#CBA77A]/10 transition-colors duration-500">
                    <Icon className="w-5 h-5 md:w-6 md:h-6 text-[#CBA77A]" strokeWidth={1.4} />
                  </div>

                  {/* Title */}
                  <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[#1A1C19] tracking-[-0.015em] leading-tight mb-4">
                    {s.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[#5A6153] text-sm md:text-[15px] leading-relaxed font-light mb-8">
                    {s.description}
                  </p>

                  {/* Discreet link */}
                  <button
                    type="button"
                    onClick={() => navigate(s.link)}
                    data-testid={`service-link-${i}`}
                    className="group/btn inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.22em] uppercase text-[#1A1C19]/70 hover:text-[#CBA77A] transition-colors duration-300"
                  >
                    <span>En savoir plus</span>
                    <ArrowUpRight className="w-3 h-3 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" strokeWidth={2} />
                  </button>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── EV Charging ──────────────────────────────── */}
      <section
        data-testid="ev-charging-section"
        className="py-28 md:py-40 bg-[#1A1C19] relative overflow-hidden"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: easeLuxe }}
            className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center"
          >
            <div className="order-2 md:order-1">
              <span className="overline">Ecoresponsable</span>
              <h3 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-white mt-4 tracking-[-0.02em] leading-[1.02]">
                Borne de
                <span className="italic text-white/70"> recharge electrique</span>
              </h3>
              <p className="text-white/55 text-sm md:text-base leading-relaxed font-light mt-6 max-w-md">
                Une borne 7,4 kW est disponible sur le parking prive du Domaine du Pilat,
                compatible avec tous les vehicules electriques.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#CBA77A]/15 border border-[#CBA77A]/30">
                <Zap className="w-4 h-4 text-[#CBA77A]" strokeWidth={1.5} />
                <span className="text-[#CBA77A] text-xs font-semibold tracking-[0.18em] uppercase">
                  5&nbsp;EUR / nuit &middot; option a reserver
                </span>
              </div>
              <p className="text-white/35 text-xs mt-10 italic font-light">
                Voyagez vert, sejournez au Domaine du Pilat.
              </p>
            </div>
            <div className="order-1 md:order-2">
              <div className="rounded-[1.5rem] md:rounded-[2rem] overflow-hidden shadow-[0_24px_80px_-16px_rgba(0,0,0,0.5)]">
                <img
                  src="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900"
                  alt="Borne de recharge electrique"
                  className="w-full h-72 md:h-[480px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Oeufs frais du domaine ───────────────────── */}
      <section
        data-testid="eggs-section"
        className="py-28 md:py-40 bg-[#F9F8F6]"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: easeLuxe }}
            className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center"
          >
            <div>
              <div className="rounded-[1.5rem] md:rounded-[2rem] overflow-hidden shadow-[0_24px_80px_-16px_rgba(26,28,25,0.15)]">
                <img
                  src="https://images.unsplash.com/photo-1518569656558-1f25e69d93d7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900"
                  alt="Oeufs frais du domaine"
                  className="w-full h-72 md:h-[480px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div>
              <span className="overline">Saveurs du Pilat</span>
              <h3 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-[#1A1C19] mt-4 tracking-[-0.02em] leading-[1.02]">
                Oeufs frais
                <span className="italic text-[#5A6153]"> du domaine</span>
              </h3>
              <p className="text-[#5A6153] text-sm md:text-base leading-relaxed font-light mt-6 max-w-md">
                Reveillez-vous avec le gout authentique du Pilat. Nos poules vivent en plein air
                sur le domaine et nous vous reservons des oeufs fraichement pondus,
                deposes au gite le matin de votre arrivee.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#CBA77A]/15 border border-[#CBA77A]/30">
                <Egg className="w-4 h-4 text-[#CBA77A]" strokeWidth={1.5} />
                <span className="text-[#1A1C19] text-xs font-semibold tracking-[0.18em] uppercase">
                  1&nbsp;EUR / oeuf &middot; jusqu&apos;a 150 unites
                </span>
              </div>
              <p className="text-[#5A6153]/70 text-xs mt-10 italic font-light">
                Une attention bucolique, un petit-dejeuner inoubliable.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
